import * as Icon from 'base/ui/components/Icons';

import { Form, Radio, Spin, message } from 'antd';
import { useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import BoxContainer from 'base/ui/components/BoxContainer';
import CustomBreadcrumb from 'base/ui/components/CustomBreadcrumb';
import CustomButton from 'base/ui/components/Button';
import CustomInput from 'base/ui/components/CustomInput';
import { SearchForm, SearchItem } from 'pages/Components/SearchBox';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { wasteGroupPresenter, wasteGroupStore } from 'stores/root_store';
import { WasteGroupingContractModel } from 'services/RawData/wasteGroup_services';
import { usePermission } from 'hooks/usePermission';

const createWasteGroupingContractDetailPage = () => {
  return observer(() => {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Kiểm tra phân quyền
    if (id) {
      usePermission('EDIT');
    } else {
      usePermission('CREATE');
    }
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const saveModeRef = useRef<'save' | 'save-continue'>('save');

    const initialValues = useMemo(
      () => ({
        actived: true,
      }),
      [],
    );

    const loadDetail = async (contractId: string) => {
      setLoading(true);
      await wasteGroupPresenter.getWasteGroupDetail(wasteGroupStore, contractId);
      if (wasteGroupStore.detailResponse.isSuccess && wasteGroupStore.detailResponse.data) {
        const detail = wasteGroupStore.detailResponse.data;
        form.setFieldsValue({
          wasteGroupingContractCode: detail.wasteGroupingContractCode,
          wasteGroupingContractName: detail.wasteGroupingContractName,
          actived: detail.actived,
        });
      } else {
        navigate('/MasterData/WasteGroupContract');
        message.error(wasteGroupStore.detailResponse.message);
      }
      setLoading(false);
    };

    useEffect(() => {
      if (id) {
        loadDetail(id);
      } else {
        form.setFieldsValue(initialValues);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleSubmit = async (values: WasteGroupingContractModel) => {
      setLoading(true);
      if (id) {
        await wasteGroupPresenter.updateWasteGroup(wasteGroupStore, {
          ...values,
          wasteGroupingContractId: id,
        });
      } else {
        await wasteGroupPresenter.createWasteGroup(wasteGroupStore, values);
      }
      setLoading(false);

      if (wasteGroupStore.crudResponse.isSuccess) {
        message.success(wasteGroupStore.crudResponse.message);
        if (saveModeRef.current === 'save' || id) {
          navigate('/MasterData/WasteGroupContract');
        } else {
          form.resetFields();
          form.setFieldsValue(initialValues);
        }
      } else {
        message.error(wasteGroupStore.crudResponse.message);
      }
    };

    const ButtonBar = () => (
      <>
        <CustomButton
          icon={<Icon.Download className="!fill-[#262626]" />}
          disabled={loading}
          loading={loading && saveModeRef.current === 'save'}
          onClick={() => {
            saveModeRef.current = 'save';
            form.submit();
          }}
        >
          {t('btn.save')}
        </CustomButton>
        <CustomButton
          type="primary"
          icon={<Icon.Download />}
          disabled={loading}
          loading={loading && saveModeRef.current === 'save-continue'}
          onClick={() => {
            saveModeRef.current = 'save-continue';
            form.submit();
          }}
        >
          {id ? t('btn.save-update') : t('btn.save-continue-create')}
        </CustomButton>
      </>
    );

    return (
      <div className="flex flex-col h-full">
        <CustomBreadcrumb
          pagetitle={[
            {
              title: t('masterdata.waste-grouping-contract.title'),
              link: '/MasterData/WasteGroupContract',
            },
            {
              title: id ? t('page-title.update') : t('page-title.create'),
              link: id ? `/MasterData/WasteGroupContract/${id}` : '/MasterData/WasteGroupContract/Create',
            },
          ]}
          extra={<div className="custom__mobile--save flex space-x-2"><ButtonBar /></div>}
        />
        <BoxContainer fullHeight>
          <Spin spinning={loading}>
            <SearchForm
              column="col-2"
              form={form}
              initialValues={initialValues}
              onFinish={handleSubmit}
              className="gap-6"
            >
              <SearchItem
                name="wasteGroupingContractName"
                label={t('masterdata.waste-grouping-contract.sub-group-name')}
                rules={[
                  {
                    required: true,
                    message: `${t('message.required-input')} ${t('masterdata.waste-grouping-contract.sub-group-name')}`,
                  },
                ]}
              >
                <CustomInput placeholder={t('masterdata.waste-grouping-contract.sub-group-placeholder')} />
              </SearchItem>
              {id && (
                <SearchItem name="actived" label={t('masterdata.waste-grouping-contract.status')}>
                  <Radio.Group className="flex gap-6">
                    <Radio value={true}>{t('radio-btn.active')}</Radio>
                    <Radio value={false}>{t('radio-btn.in-active')}</Radio>
                  </Radio.Group>
                </SearchItem>
              )}
            </SearchForm>
          </Spin>
        </BoxContainer>
      </div>
    );
  });
};

export default createWasteGroupingContractDetailPage;

